import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { createGeneralApi } from '../tbsGeneralApi'
import { resolveLayout } from '../ScreenGenerator'
import { cn, toStyleObject } from '@/lib/utils'

export default function TabsField({ id, elements, visible: fieldVisible, onEvent, onRenderFields, compoGroup, userData, class: cssClass, style: rawStyle }) {
  // `visible: false` (JSON, sur le champ TabsField lui-même) → display:none posé directement sur
  // [data-tbs-root] : voir ChampField.jsx pour le détail (show()/hide() ciblent ce même élément).
  const visibilityStyle = fieldVisible === false ? { display: 'none' } : undefined
  const cssStyle = toStyleObject(rawStyle)
  const [activeTab, setActiveTab]         = useState(String(0))
  const [hiddenSheets, setHiddenSheets]   = useState(new Set())
  const [badgeOverrides, setBadgeOverrides] = useState({})
  const [labelOverrides, setLabelOverrides] = useState({})

  const activeTabRef    = useRef(activeTab)
  const compoGroupRef   = useRef(compoGroup ?? null)
  const userDataRef     = useRef(userData ?? null)
  const hiddenSheetsRef = useRef(new Set())
  const allRef          = useRef(elements ?? [])
  useLayoutEffect(() => { activeTabRef.current = activeTab; hiddenSheetsRef.current = hiddenSheets })

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    const getVisible = hidden => allRef.current.filter(el => el.visible !== false && !hidden.has(el.id))
    window.tbsComponents[id] = {
      type: 'tabs',
      ...createGeneralApi(id, userDataRef.current),
      getActiveTab:   () => Number(activeTabRef.current),
      setActiveTab:   i => { const s = String(i); activeTabRef.current = s; setActiveTab(s) },
      getCompoGroup:  () => compoGroupRef.current,
      setCompoGroup:  v => { compoGroupRef.current = v },
      // activeTabRef/hiddenSheetsRef assignés de façon synchrone avant chaque setState : sans
      // cela, des appels chaînés (ex. showSheet() puis hideSheet()) liraient encore l'ancien état
      // via les refs (resynchronisées seulement au rendu suivant par le useLayoutEffect ci-dessus).
      showSheet:      sheetId => {
        const activeEl = getVisible(hiddenSheetsRef.current)[Number(activeTabRef.current)]
        const newHidden = new Set(hiddenSheetsRef.current); newHidden.delete(sheetId)
        const newVisible = getVisible(newHidden)
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        if (activeEl) {
          const i = newVisible.findIndex(el => el.id === activeEl.id)
          if (i >= 0) { const s = String(i); activeTabRef.current = s; setActiveTab(s) }
        }
      },
      hideSheet:      sheetId => {
        const activeEl = getVisible(hiddenSheetsRef.current)[Number(activeTabRef.current)]
        const newHidden = new Set([...hiddenSheetsRef.current, sheetId])
        const newVisible = getVisible(newHidden)
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        if (activeEl?.id === sheetId) { activeTabRef.current = '0'; setActiveTab('0') }
        else if (activeEl) {
          const i = newVisible.findIndex(el => el.id === activeEl.id)
          if (i >= 0) { const s = String(i); activeTabRef.current = s; setActiveTab(s) }
        }
      },
      hideSheets:     exceptId => {
        const newHidden = new Set(allRef.current.map(el => el.id).filter(i => i !== exceptId))
        const newVisible = getVisible(newHidden)
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        const idx = newVisible.findIndex(el => el.id === exceptId)
        const s = String(idx >= 0 ? idx : 0)
        activeTabRef.current = s
        setActiveTab(s)
      },
      setActiveSheet: sheetId => {
        const newVisible = getVisible(hiddenSheetsRef.current)
        const idx = newVisible.findIndex(el => el.id === sheetId)
        if (idx >= 0) {
          const s = String(idx)
          activeTabRef.current = s
          setActiveTab(s)
          onEvent(id, 'onActiveTab', idx)
        }
      },
      getListOfSheets: () => allRef.current.map(el => el.id),
      setSheetTitle:  (idSheet, title) => setLabelOverrides(prev => ({ ...prev, [idSheet]: title })),
    }
    // Chaque onglet (sheet) s'enregistre aussi sous son propre id — méthodes générales
    // uniquement (show/hide/addCss/...), ciblant son panneau via data-tbs-root={el.id}.
    // Toujours monté (visible ou non, actif ou non — même principe que les champs enfants),
    // donc enregistré indépendamment de l'onglet actif/masqué.
    const sheets = allRef.current
    for (const el of sheets) {
      if (!el.id) continue
      window.tbsComponents[el.id] = {
        type: 'tabsheet',
        ...createGeneralApi(el.id, el.userData),
        setBadge: text  => setBadgeOverrides(prev => ({ ...prev, [el.id]: text })),
        setLabel: label => setLabelOverrides(prev => ({ ...prev, [el.id]: label })),
      }
    }
    return () => {
      delete window.tbsComponents[id]
      for (const el of sheets) { if (el.id) delete window.tbsComponents[el.id] }
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  const all     = elements ?? []
  const visible = all.filter(el => el.visible !== false && !hiddenSheets.has(el.id))
  const hidden  = all.filter(el => el.visible === false  ||  hiddenSheets.has(el.id))

  return (
    <>
      <Tabs id={id} data-tbs-root={id} className={cssClass || undefined} style={{ ...cssStyle, ...visibilityStyle }} value={activeTab} onValueChange={v => { setActiveTab(v); onEvent(id, 'onActiveTab', Number(v)) }}>
        <TabsList>
          {visible.map((el, i) => (
            <TabsTrigger key={el.id ?? i} id={`${id}-tab-${i}`} value={String(i)} className="flex items-center gap-1.5"
              data-tbs-tab-trigger-id={el.id ?? i}
              onClick={() => onEvent(id, 'onClick', i)}>
              {el.icon && <i className={`${el.icon}`} aria-hidden="true" />}
              {labelOverrides[el.id] ?? el.title}
              <Badge value={badgeOverrides[el.id] ?? el.badge} />
            </TabsTrigger>
          ))}
        </TabsList>
        {visible.map((el, i) => (
          <TabsContent key={el.id ?? i} id={el.id} value={String(i)} className={cn('pt-4 bg-[hsl(var(--card))]', el.class)} forceMount
            data-tbs-tab-panel-id={el.id ?? i} data-tbs-root={el.id}
            style={{ ...toStyleObject(el.style), ...(activeTab !== String(i) ? { display: 'none' } : undefined) }}>
            {onRenderFields(el.fields, resolveLayout(el))}
          </TabsContent>
        ))}
      </Tabs>
      {hidden.map(el => (
        <div key={el.id} id={el.id} data-tbs-root={el.id} className={el.class || undefined} style={{ ...toStyleObject(el.style), display: 'none' }}>
          {onRenderFields(el.fields, resolveLayout(el))}
        </div>
      ))}
    </>
  )
}
